import { superUserId } from "@/lib/constants/roles";
import { User } from "@/types/user";

/**
 * Check if a user has admin role
 */
export function isAdminUser(user: User): boolean {
  return user.roles.some((role) => role.role.name.toLowerCase() === "admin");
}

/**
 * Check if a user is the first admin (super admin)
 */
export function isFirstAdminUser(user: User): boolean {
  return user.email === superUserId;
}

/**
 * Check if admin operations are allowed on a user
 * Returns false if the target user is admin or if the current user is not admin
 */
export function canModifyUser(currentUser: User, targetUser: User): boolean {
  // Only admins can modify users
  if (!isAdminUser(currentUser)) {
    return false;
  }

  // Cannot modify admin users
  if (isAdminUser(targetUser)) {
    return false;
  }

  return true;
}

/**
 * Check if admin operations are allowed on a user by ID
 * This is used when we only have the user ID and need to check permissions
 */
export function canModifyUserById(
  currentUser: User,
  targetUserId: string
): boolean {
  // Only admins can modify users
  if (!isAdminUser(currentUser)) {
    return false;
  }

  // Cannot modify the current user (self-modification protection)
  if (currentUser.id === targetUserId) {
    return false;
  }

  return true;
}

/**
 * Get admin protection message
 */
export function getAdminProtectionMessage(
  action: "edit" | "delete" | "toggle"
): string {
  return `Cannot ${action} admin users. Admin users are protected from modification.`;
}

/**
 * Check if user can be deleted
 */
export function canDeleteUser(currentUser: User, targetUser: User): boolean {
  return canModifyUser(currentUser, targetUser);
}

/**
 * Check if user status can be toggled
 */
export function canToggleUserStatus(
  currentUser: User,
  targetUser: User
): boolean {
  return canModifyUser(currentUser, targetUser);
}

/**
 * Check if user can be edited
 */
export function canEditUser(currentUser: User, targetUser: User): boolean {
  return canModifyUser(currentUser, targetUser);
}
